/**
 * @description Channel Partner branding editor (LYN-309)
 *
 * Key features:
 * - Identity / Colors / Assets / Meta / Preview — same sections as SO Branding
 * - Preview displayName is identity.platformName only — never company name
 * - Three uploads: logo, app icon, favicon
 *
 * @dependencies lucide-react
 *
 * @notes
 * - Do not infer platformName from channel_partners.name
 * - Empty preview letter uses platformName or a generic mark, never platform yellow
 * - Assets are upload/delete only — save omits asset URLs
 */

import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  Image,
  Loader2,
  Moon,
  Save,
  Sun,
  Trash2,
  Upload,
} from 'lucide-react';

const GENERIC_PRIMARY = '#e4e4e7';
const GENERIC_SECONDARY = '#a1a1aa';
const GENERIC_THEME = '#0a0a0b';

export default function ChannelPartnerBrandingEditor({
  branding = {},
  onSave,
  onUpload,
  onDelete,
}) {
  const [identity, setIdentity] = useState({
    platformName: branding.identity?.platformName || '',
    documentTitle: branding.identity?.documentTitle || '',
    tagline: branding.identity?.tagline || '',
  });
  const [colors, setColors] = useState({
    mode: branding.colors?.mode || 'dark',
    primaryColor: branding.colors?.primaryColor || GENERIC_PRIMARY,
    secondaryColor: branding.colors?.secondaryColor || GENERIC_SECONDARY,
  });
  const [meta, setMeta] = useState({
    themeColor: branding.meta?.themeColor || GENERIC_THEME,
    description: branding.meta?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    setIdentity({
      platformName: branding.identity?.platformName || '',
      documentTitle: branding.identity?.documentTitle || '',
      tagline: branding.identity?.tagline || '',
    });
    setColors({
      mode: branding.colors?.mode || 'dark',
      primaryColor: branding.colors?.primaryColor || GENERIC_PRIMARY,
      secondaryColor: branding.colors?.secondaryColor || GENERIC_SECONDARY,
    });
    setMeta({
      themeColor: branding.meta?.themeColor || GENERIC_THEME,
      description: branding.meta?.description || '',
    });
  }, [branding]);

  const logoUrl = branding.assets?.logoUrl || null;
  const logoMarkUrl = branding.assets?.logoMarkUrl || null;
  const faviconUrl = branding.assets?.faviconUrl || null;
  const assetsBusy = uploading !== null;
  const isIdentityValid = identity.platformName.trim().length > 0;
  const displayName = identity.platformName.trim();

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const cleanIdentity = { platformName: identity.platformName.trim() };
      if (identity.documentTitle.trim()) cleanIdentity.documentTitle = identity.documentTitle.trim();
      if (identity.tagline.trim()) cleanIdentity.tagline = identity.tagline.trim();
      await onSave({ identity: cleanIdentity, colors, meta });
      setSaveResult('success');
      setTimeout(() => setSaveResult(null), 2000);
    } catch (err) {
      setSaveResult(err.response?.data?.error?.message || err.message || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const runUpload = async (kind, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(kind);
    setUploadError(null);
    try {
      await onUpload(kind, file);
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || err.message || 'Upload failed');
    } finally {
      setUploading(null);
      event.target.value = '';
    }
  };

  const runDelete = async (kind) => {
    setUploading(kind);
    setUploadError(null);
    try {
      await onDelete(kind);
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || err.message || 'Delete failed');
    } finally {
      setUploading(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Platform brand</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Identity paints the live app. Company name is edited on My Company.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || assetsBusy || !isIdentityValid}
          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : saveResult === 'success' ? (
            <Check className="w-3.5 h-3.5" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          {saving ? 'Saving...' : saveResult === 'success' ? 'Saved' : 'Save Changes'}
        </button>
      </div>

      {typeof saveResult === 'string' && saveResult !== 'success' && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{saveResult}</span>
        </div>
      )}

      {uploadError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{uploadError}</span>
        </div>
      )}

      <div className="space-y-6">
        <section className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Identity</p>
          </div>
          <div className="px-4 py-3 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Platform Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={identity.platformName}
                onChange={(e) => setIdentity((prev) => ({ ...prev, platformName: e.target.value }))}
                placeholder="App name"
                className={`w-full max-w-sm px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                  identity.platformName.trim().length === 0 ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              <p className="text-xs text-gray-400 mt-1">
                Platform / app name. Not the company name.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
              <input
                type="text"
                value={identity.documentTitle}
                onChange={(e) => setIdentity((prev) => ({ ...prev, documentTitle: e.target.value }))}
                placeholder={identity.platformName || 'Browser tab title'}
                className="w-full max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
              <input
                type="text"
                value={identity.tagline}
                onChange={(e) => setIdentity((prev) => ({ ...prev, tagline: e.target.value }))}
                className="w-full max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
            </div>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Colors</p>
          </div>
          <div className="px-4 py-3 space-y-4">
            <div className="grid grid-cols-2 gap-4 max-w-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={colors.primaryColor}
                    onChange={(e) => setColors((prev) => ({ ...prev, primaryColor: e.target.value }))}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={colors.primaryColor}
                    onChange={(e) => setColors((prev) => ({ ...prev, primaryColor: e.target.value }))}
                    className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={colors.secondaryColor}
                    onChange={(e) => setColors((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={colors.secondaryColor}
                    onChange={(e) => setColors((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                    className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm font-mono"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Mode</label>
              <div className="flex items-center gap-2 max-w-xs">
                <button
                  type="button"
                  onClick={() => setColors((prev) => ({ ...prev, mode: 'dark' }))}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${
                    colors.mode === 'dark'
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  <Moon className="w-3.5 h-3.5" />
                  Dark
                </button>
                <button
                  type="button"
                  onClick={() => setColors((prev) => ({ ...prev, mode: 'light' }))}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${
                    colors.mode === 'light'
                      ? 'bg-white text-gray-900 border-gray-900 ring-1 ring-gray-900'
                      : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  <Sun className="w-3.5 h-3.5" />
                  Light
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Assets</p>
          </div>
          <div className="px-4 py-3 space-y-5">
            <AssetRow
              label="Logo"
              hint="Wordmark. PNG or JPEG recommended for share cards."
              url={logoUrl}
              kind="logo"
              accept="image/svg+xml,image/png,image/jpeg"
              busy={assetsBusy}
              uploading={uploading === 'logo'}
              onUpload={runUpload}
              onDelete={runDelete}
            />
            <AssetRow
              label="App Icon"
              hint="Square symbol for the collapsed sidebar and home screen."
              url={logoMarkUrl}
              kind="logoMark"
              accept="image/svg+xml,image/png,image/jpeg"
              busy={assetsBusy}
              uploading={uploading === 'logoMark'}
              onUpload={runUpload}
              onDelete={runDelete}
            />
            <AssetRow
              label="Favicon"
              hint="Browser tab icon. ICO, PNG, or SVG."
              url={faviconUrl}
              kind="favicon"
              accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml"
              busy={assetsBusy}
              uploading={uploading === 'favicon'}
              onUpload={runUpload}
              onDelete={runDelete}
            />
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Meta</p>
          </div>
          <div className="px-4 py-3 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Theme Color</label>
              <div className="flex items-center gap-2 max-w-xs">
                <input
                  type="color"
                  value={meta.themeColor}
                  onChange={(e) => setMeta((prev) => ({ ...prev, themeColor: e.target.value }))}
                  className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={meta.themeColor}
                  onChange={(e) => setMeta((prev) => ({ ...prev, themeColor: e.target.value }))}
                  className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={meta.description}
                onChange={(e) => setMeta((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Preview</p>
          </div>
          <div className="px-4 py-4">
            <div
              className="rounded-t-lg px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: colors.mode === 'dark' ? '#0f172a' : '#f8fafc' }}
            >
              <div className="flex items-center gap-2.5">
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="h-7 w-auto" />
                ) : (
                  <div
                    className="w-7 h-7 rounded flex items-center justify-center text-xs font-bold"
                    style={{
                      backgroundColor: colors.primaryColor,
                      color: colors.mode === 'dark' ? '#0f172a' : '#ffffff',
                    }}
                  >
                    {displayName.charAt(0) || '·'}
                  </div>
                )}
                <span
                  data-testid="branding-preview-name"
                  className="text-sm font-semibold"
                  style={{ color: colors.mode === 'dark' ? '#f8fafc' : '#0f172a' }}
                >
                  {displayName}
                </span>
              </div>
            </div>
            <div
              className="px-4 py-4 rounded-b-lg border-t"
              style={{
                backgroundColor: colors.mode === 'dark' ? '#1e293b' : '#ffffff',
                borderColor: colors.mode === 'dark' ? '#334155' : '#e2e8f0',
              }}
            >
              <p className="text-xs" style={{ color: colors.mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                {identity.tagline || 'Tagline appears here'}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function AssetRow({
  label,
  hint,
  url,
  kind,
  accept,
  busy,
  uploading,
  onUpload,
  onDelete,
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <p className="text-xs text-gray-400 mb-2">{hint}</p>
      {url ? (
        <div className="flex items-center gap-4">
          <div className="w-32 h-16 border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden p-2">
            <img src={url} alt={label} className="max-w-full max-h-full object-contain" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={`inline-flex items-center gap-1.5 text-xs font-medium ${busy ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 cursor-pointer'}`}>
              <Upload className="w-3.5 h-3.5" />
              Replace
              <input type="file" accept={accept} onChange={(e) => onUpload(kind, e)} disabled={busy} className="hidden" />
            </label>
            <button
              type="button"
              onClick={() => onDelete(kind)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs text-red-600 font-medium disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Remove
            </button>
          </div>
        </div>
      ) : (
        <label className={`flex items-center gap-3 p-4 border-2 border-dashed border-gray-200 rounded-lg max-w-sm ${busy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300'}`}>
          {uploading ? <Loader2 className="w-6 h-6 text-gray-400 animate-spin" /> : <Image className="w-6 h-6 text-gray-400" />}
          <div>
            <p className="text-sm font-medium text-gray-700">Upload {label.toLowerCase()}</p>
          </div>
          <input type="file" accept={accept} onChange={(e) => onUpload(kind, e)} disabled={busy} className="hidden" />
        </label>
      )}
    </div>
  );
}
